import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  convertWeight,
  WeightUnit,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import type { PurchaseFreightBookingResponse } from "@redotech/redo-model/freight/freight-booking";
import type { FreightDeliveryAccessorialPresetRow } from "@redotech/redo-model/freight/freight-delivery-accessorial-preset";
import type { FreightHandlingUnitRow } from "@redotech/redo-model/freight/freight-handling-unit";
import type { FreightPickupAccessorialPresetRow } from "@redotech/redo-model/freight/freight-pickup-accessorial-preset";
import type { FreightPickupWindowRow } from "@redotech/redo-model/freight/freight-pickup-window";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { CheckboxValue } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalFooter } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { DisabledTooltip } from "@redotech/redo-web/tooltip/disabled-tooltip";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoMerchantRpcClientContext } from "../rpc-client";
import { BookStep } from "./book-step";
import * as styles from "./freight-booking-modal.module.css";
import { FreightBookingStepper } from "./freight-booking-stepper";
import {
  createInitialFreightBookingState,
  FreightBookingState,
  FreightBookingStep,
  freightBookingSteps,
  FreightProduct,
  FreightStepData,
  RouteStepData,
  validateFreightStep,
  validateRouteStep,
} from "./freight-booking-types";
import { FreightStep } from "./freight-step";
import { QuotesStep } from "./quotes-step";
import { RouteStep } from "./route-step";

const REDO_SKU_PREFIX = "x-redo";

export function fulfillmentGroupToFreightProducts(
  group: FulfillmentGroupData,
): FreightProduct[] {
  return group.items
    .filter((item) => !item.sku?.startsWith(REDO_SKU_PREFIX))
    .map((item) => ({
      id: item.variantId || item.sku || item.externalId,
      name: item.productTitle,
      sku: item.sku ?? undefined,
      itemWeight: item.weight,
      quantity: item.unfulfilledQuantity,
    }));
}

interface FreightBookingModalProps {
  isOpen: boolean;
  onClose(): void;
  onPurchase(response: PurchaseFreightBookingResponse): void;
  originAddress: FulfillmentOrderAddress;
  destinationAddress: FulfillmentOrderAddress;
  freightProducts: FreightProduct[];
  balance: string;
  customerReference1?: string;
}

export const FreightBookingModal = memo(function FreightBookingModal({
  isOpen,
  onClose,
  onPurchase,
  originAddress: initialOriginAddress,
  destinationAddress: initialDestinationAddress,
  freightProducts,
  balance,
  customerReference1,
}: FreightBookingModalProps) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const [currentStep, setCurrentStep] = useState<FreightBookingStep>("route");

  const presetsLoad = useLoad(async () => {
    const [
      pickupWindowsResult,
      handlingUnitsResult,
      pickupAccessorialPresetsResult,
      deliveryAccessorialPresetsResult,
    ] = await Promise.all([
      rpcClient.getFreightPickupWindows({}),
      rpcClient.getFreightHandlingUnits({}),
      rpcClient.getFreightPickupAccessorialPresets({}),
      rpcClient.getFreightDeliveryAccessorialPresets({}),
    ]);
    return {
      pickupWindows: pickupWindowsResult.pickupWindows,
      handlingUnits: handlingUnitsResult.handlingUnits,
      pickupAccessorialPresets: pickupAccessorialPresetsResult.presets,
      deliveryAccessorialPresets: deliveryAccessorialPresetsResult.presets,
    };
  }, [rpcClient]);

  const pickupWindows: FreightPickupWindowRow[] =
    presetsLoad.value?.pickupWindows ?? [];
  const savedHandlingUnits: FreightHandlingUnitRow[] =
    presetsLoad.value?.handlingUnits ?? [];
  const pickupAccessorialPresets: FreightPickupAccessorialPresetRow[] =
    presetsLoad.value?.pickupAccessorialPresets ?? [];
  const deliveryAccessorialPresets: FreightDeliveryAccessorialPresetRow[] =
    presetsLoad.value?.deliveryAccessorialPresets ?? [];

  const [state, setState] = useState<FreightBookingState>(() =>
    createInitialFreightBookingState(
      freightProducts,
      [],
      [],
      [],
      initialOriginAddress,
      initialDestinationAddress,
      customerReference1,
    ),
  );

  // Apply loaded presets/defaults once the fetch completes
  const [presetsApplied, setPresetsApplied] = useState(false);
  if (presetsLoad.value && !presetsApplied) {
    setPresetsApplied(true);
    setState(
      createInitialFreightBookingState(
        freightProducts,
        presetsLoad.value.pickupWindows,
        presetsLoad.value.pickupAccessorialPresets,
        presetsLoad.value.deliveryAccessorialPresets,
        initialOriginAddress,
        initialDestinationAddress,
        customerReference1,
      ),
    );
  }

  const [bookingConfirmed, setBookingConfirmed] =
    useState<CheckboxValue>(false);
  const [purchasing, setPurchasing] = useState(false);
  const [originAddress, setOriginAddress] = useState(initialOriginAddress);
  const [destinationAddress, setDestinationAddress] = useState(
    initialDestinationAddress,
  );

  const currentStepIndex = freightBookingSteps.indexOf(currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === freightBookingSteps.length - 1;

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setBookingConfirmed(false);
      setCurrentStep(freightBookingSteps[currentStepIndex - 1]!);
    }
  }, [currentStepIndex]);

  const handleContinue = useCallback(() => {
    if (currentStepIndex < freightBookingSteps.length - 1) {
      setBookingConfirmed(false);
      setCurrentStep(freightBookingSteps[currentStepIndex + 1]!);
    }
  }, [currentStepIndex]);

  const handleGoToStep = useCallback((step: FreightBookingStep) => {
    setBookingConfirmed(false);
    setCurrentStep(step);
  }, []);

  const setRouteData = useCallback((route: RouteStepData) => {
    setState((prev) => ({ ...prev, route }));
  }, []);

  const setFreightData = useCallback((freight: FreightStepData) => {
    setState((prev) => ({ ...prev, freight }));
  }, []);

  const setSelectedQuote = useCallback(
    (selectedQuote: FreightBookingState["selectedQuote"]) => {
      setState((prev) => ({ ...prev, selectedQuote }));
    },
    [],
  );

  const handleBookFreight = useCallback(async () => {
    if (!state.selectedQuote || !team.accountId) {
      return;
    }
    setPurchasing(true);
    try {
      const productsById = new Map(
        state.freight.products.map((p) => [p.id, p]),
      );
      const handlingUnitList = state.freight.handlingUnits
        .filter((unit) => unit.assignedProducts.length > 0)
        .map((unit) => {
          if (!unit.freightClass) {
            throw new Error("Freight class not set for handling unit");
          }
          const shippedItems = unit.assignedProducts.map((ap) => {
            const product = productsById.get(ap.productId);
            if (!product) {
              throw new Error(
                `Product not found for assigned product ${ap.productId}`,
              );
            }

            const weightLbs =
              convertWeight(product.itemWeight, WeightUnit.POUND).value *
              ap.quantity;
            return {
              description: product.name,
              quantity: ap.quantity,
              weightLbs,
              dimensions: product?.dimensions,
            };
          });
          const totalWeightLbs = shippedItems.reduce(
            (sum, item) => sum + item.weightLbs,
            0,
          );
          return {
            type: unit.type,
            quantity: unit.quantity,
            stackable: unit.stackable,
            freightClass: unit.freightClass!,
            totalWeightLbs,
            dimensions: unit.dimensions,
            shippedItems,
          };
        });

      const response = await rpcClient.purchaseFreightBooking({
        quote: state.selectedQuote,
        originAddress,
        destinationAddress,
        pickupDateTime: state.route.pickupDateTime!.toString(),
        closeTime: state.route.closeTime!.toString(),
        pickupLocationType: state.route.pickupLocationType,
        deliveryLocationType: state.route.deliveryLocationType,
        pickupAccessorials: {
          ...state.route.pickupAccessorials,
          instructions:
            state.route.pickupAccessorials.instructions || undefined,
        },
        deliveryAccessorials: {
          ...state.route.deliveryAccessorials,
          instructions:
            state.route.deliveryAccessorials.instructions || undefined,
        },
        handlingUnitList,
        customerReference1: state.freight.customerReference1 || undefined,
        customerReference2: state.freight.customerReference2 || undefined,
        specialInstructions: state.freight.specialInstructions || undefined,
      });

      onPurchase(response);
      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to book freight shipment";
      toast(message, { variant: "error" });
    } finally {
      setPurchasing(false);
    }
  }, [
    state,
    rpcClient,
    originAddress,
    destinationAddress,
    onPurchase,
    onClose,
    team.accountId,
  ]);

  const minimumBalance =
    team.settings?.outboundLabels?.billing?.autoReload?.floor ?? 0;

  const insufficientFundsForFreight = useMemo(() => {
    if (!state.selectedQuote) {
      return false;
    }
    return (
      Number(balance) - state.selectedQuote.totalPrice.value <
      Number(minimumBalance)
    );
  }, [balance, minimumBalance, state.selectedQuote]);

  const continueDisabledReason = useMemo((): string | undefined => {
    if (currentStep === "route") {
      return validateRouteStep(state.route, originAddress, destinationAddress);
    }
    if (currentStep === "freight") {
      return validateFreightStep(state.freight);
    }
    if (currentStep === "quotes" && !state.selectedQuote) {
      return "Please select a quote";
    }
    return undefined;
  }, [
    currentStep,
    state.route,
    state.freight,
    state.selectedQuote,
    originAddress,
    destinationAddress,
  ]);

  return (
    <RedoBaseModal
      isOpen={isOpen}
      onModalCloseRequested={onClose}
      otherClasses={[styles.fullScreen]}
    >
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="primary"
        justify="space-between"
        p="lg"
      >
        <Text fontSize="sm" fontWeight="semibold">
          Book freight
        </Text>
        <FreightBookingStepper
          currentStep={currentStep}
          steps={freightBookingSteps}
        />
        <RedoButton
          hierarchy="tertiary"
          IconLeading={CloseIcon}
          onClick={onClose}
          size="md"
        />
      </Flex>
      <div className={styles.content}>
        {currentStep === "route" && (
          <RouteStep
            data={state.route}
            deliveryAccessorialPresets={deliveryAccessorialPresets}
            destinationAddress={destinationAddress}
            onDestinationAddressChange={setDestinationAddress}
            onOriginAddressChange={setOriginAddress}
            originAddress={originAddress}
            pickupAccessorialPresets={pickupAccessorialPresets}
            pickupWindows={pickupWindows}
            setData={setRouteData}
          />
        )}
        {currentStep === "freight" && (
          <FreightStep
            data={state.freight}
            savedHandlingUnits={savedHandlingUnits}
            setData={setFreightData}
          />
        )}
        {currentStep === "quotes" && (
          <QuotesStep
            destinationAddress={destinationAddress}
            onGoToStep={handleGoToStep}
            onSelectQuote={setSelectedQuote}
            originAddress={originAddress}
            selectedQuoteId={state.selectedQuote?.quoteId}
            state={state}
          />
        )}
        {currentStep === "book" && state.selectedQuote && (
          <BookStep
            destinationAddress={destinationAddress}
            originAddress={originAddress}
            selectedQuote={state.selectedQuote}
            state={state}
          />
        )}
      </div>
      {isLastStep ? (
        <Flex align="center" justify="space-between" p="3xl">
          <Flex align="center" gap="lg">
            <RedoButton
              hierarchy="link"
              onClick={onClose}
              size="md"
              text="Cancel"
            />
            <RedoButton
              hierarchy="secondary"
              onClick={handleBack}
              size="md"
              text="Back"
            />
          </Flex>
          <Flex align="center" gap="lg">
            <RedoCheckbox
              label="I confirm the freight details are accurate"
              setValue={setBookingConfirmed}
              size="sm"
              value={bookingConfirmed}
            />
            <DisabledTooltip
              disabledMessage={
                !team.accountId
                  ? "Account not configured for freight booking"
                  : bookingConfirmed !== true
                    ? "Please confirm freight details"
                    : insufficientFundsForFreight
                      ? "Insufficient funds to purchase freight"
                      : undefined
              }
            >
              <RedoButton
                disabled={
                  !team.accountId
                    ? true
                    : bookingConfirmed !== true
                      ? true
                      : insufficientFundsForFreight
                }
                hierarchy="primary"
                onClick={handleBookFreight}
                pending={purchasing}
                size="md"
                text="Book freight"
              />
            </DisabledTooltip>
          </Flex>
        </Flex>
      ) : (
        <RedoModalFooter
          additionalContent={
            <RedoButton
              hierarchy="link"
              onClick={onClose}
              size="md"
              text="Cancel"
            />
          }
          primaryButton={{
            text: "Continue",
            onClick: handleContinue,
            size: "md",
            disabled: continueDisabledReason,
          }}
          secondaryButton={
            isFirstStep
              ? undefined
              : { text: "Back", onClick: handleBack, size: "md" }
          }
        />
      )}
    </RedoBaseModal>
  );
});
